import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  PackageRequest, PackagesView, ShipmentEmailResult, ShipmentPage, ShipmentRequest, ShipmentStatus, ShipmentStatusRequest, ShipmentView,
} from '../interfaces/sales/fulfilment.interface';

/**
 * A blob request turns the server's JSON error into a Blob: read its message back so
 * showApiError shows it (same idiom as account.service.ts downloadReport).
 */
async function rethrowBlobError(error: any): Promise<never> {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (parsed && typeof parsed.message === 'string') error.response.data = parsed;
    } catch {
      // not JSON or unreadable: keep the original error
    }
  }
  throw error;
}

/** F5: packages, shipment and packing slip of a POSTED delivery challan. */
export const FulfilmentService = {
  packages: async (i: string, challanId: string): Promise<PackagesView> =>
    (await axios.get(ENDPOINTS.SALES_FULFILMENT.PACKAGES(i, challanId))).data,
  createPackage: async (i: string, challanId: string, dto: PackageRequest): Promise<PackagesView> =>
    (await axios.post(ENDPOINTS.SALES_FULFILMENT.PACKAGES(i, challanId), dto)).data,
  updatePackage: async (i: string, challanId: string, packageId: string, dto: PackageRequest): Promise<PackagesView> =>
    (await axios.put(ENDPOINTS.SALES_FULFILMENT.PACKAGE(i, challanId, packageId), dto)).data,
  deletePackage: async (i: string, challanId: string, packageId: string): Promise<PackagesView> =>
    (await axios.delete(ENDPOINTS.SALES_FULFILMENT.PACKAGE(i, challanId, packageId))).data,
  autoPack: async (i: string, challanId: string, packageType?: string): Promise<PackagesView> =>
    (await axios.post(ENDPOINTS.SALES_FULFILMENT.AUTO_PACK(i, challanId), { packageType: packageType || undefined })).data,
  shipment: async (i: string, challanId: string): Promise<ShipmentView> =>
    (await axios.get(ENDPOINTS.SALES_FULFILMENT.SHIPMENT(i, challanId))).data,
  createShipment: async (i: string, challanId: string, dto: ShipmentRequest): Promise<ShipmentView> =>
    (await axios.post(ENDPOINTS.SALES_FULFILMENT.SHIPMENT(i, challanId), dto)).data,
  updateShipment: async (i: string, challanId: string, dto: ShipmentRequest): Promise<ShipmentView> =>
    (await axios.put(ENDPOINTS.SALES_FULFILMENT.SHIPMENT(i, challanId), dto)).data,
  changeStatus: async (i: string, challanId: string, dto: ShipmentStatusRequest): Promise<ShipmentView> =>
    (await axios.post(ENDPOINTS.SALES_FULFILMENT.SHIPMENT_STATUS(i, challanId), dto)).data,
  // F7: an explicit staff click e-mails the customer the shipment notice (never automatic).
  emailCustomer: async (i: string, challanId: string): Promise<ShipmentEmailResult> =>
    (await axios.post(ENDPOINTS.SALES_FULFILMENT.SHIPMENT_EMAIL(i, challanId))).data,
  packingSlip: async (i: string, challanId: string): Promise<Blob> => {
    try {
      return (await axios.get(ENDPOINTS.SALES_FULFILMENT.PACKING_SLIP(i, challanId), { responseType: 'blob' })).data;
    } catch (error) {
      return rethrowBlobError(error);
    }
  },
  list: async (i: string, params: { status?: ShipmentStatus; from?: string; to?: string; page?: number; size?: number } = {}): Promise<ShipmentPage> =>
    (await axios.get(ENDPOINTS.SALES_FULFILMENT.SHIPMENTS(i), { params: { page: 0, size: 50, ...params } })).data,
};
